import React, { useEffect, useState, useMemo, useContext } from "react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField } from "@mui/material";
import { UtilService } from "../../services/UtilService";
import { NotificationContext } from '../context/Notification.js';
import { FirebaseService } from '../../services/FirebaseService.js';
import moment from 'moment-timezone';

function Excel({ calc }) {
  const utilService = useMemo(() => new UtilService(), []);
  const { setNotification } = useContext(NotificationContext);
  const fire = new FirebaseService(null, setNotification);
  const [pdfData, setPdfData] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [dealerName, setDealerName] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [address, setAddress] = useState("");
  const [city, setCity] = useState("");
  const [note, setNote] = useState("");

  function formatValue(value) {
    let result = String(JSON.parse(JSON.stringify(value)));
    if (result.match(/(\d+\/\d+$)/) != null) {
      return "'" + result;
    }
    return result;
  }

  useEffect(() => {
    let data = [[calc.name]];
    calc.fields.forEach((field) => {
      if (field.type && calc[field.id].show) {
        let entry = [field.name];
        entry.push(
          formatValue(
            calc[field.id].selection
              ? calc[field.id].selection.name || calc[field.id].selection.value
              : calc[field.id].value
          )
        );
        data.push(entry);
      }
    });
    data.push(["Sub Total", utilService.formatCurrency(calc.subTotal)]);
    data.push(["Total", utilService.formatCurrency(calc.total)]);
    setPdfData(data);
  }, [calc, utilService]);

  const handleModalOpen = () => {
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  const handleCompanyNameChange = (event) => {
    setDealerName(event.target.value);
  };

  const handleAddressChange = (event) => {
    setAddress(event.target.value);
  };

  const handleStateChange = (event) => {
    setCustomerName(event.target.value);
  };

  const handleCityChange = (event) => {
    setCity(event.target.value);
  };

  const handleNoteChange = (event) => {
    setNote(event.target.value)
  };

  useEffect(() => {
    if (isModalOpen) {
      const firstFocusableElement = document.querySelector("#modal-company-name");
      if (firstFocusableElement) {
        firstFocusableElement.focus();
      }
    }
  }, [isModalOpen]);

  const handleDownloadPdf = () => {
    const doc = new jsPDF();
    const burgundyColor = "#800020";
    doc.setFillColor(burgundyColor);
    doc.setTextColor(255, 255, 255);

    const leftMargin = 10;

    const headerText = `${dealerName} - ${calc.name}`;
    const pageWidth = doc.internal.pageSize.getWidth();
    doc.rect(0, 10, pageWidth, 15, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.text(headerText, pageWidth / 2 - doc.getTextWidth(headerText) / 2, 17);


    doc.setTextColor(0, 0, 0);
    let yPositionNext = 30;

    doc.setFont("helvetica", "bold")
    doc.setFontSize(12);
    doc.text("Customer:", leftMargin, yPositionNext)
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.text(customerName, leftMargin + doc.getTextWidth("Customer:   ") + 3, yPositionNext);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    const date = moment(new Date()).tz('America/New_York').format('DD MMMM YYYY');
    doc.text(`${date}`, pageWidth - doc.getTextWidth(date) - leftMargin, 30);

    yPositionNext += 8;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.text("Address:", leftMargin, yPositionNext);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.text(address, leftMargin + doc.getTextWidth("Address:  ") + 3, yPositionNext);

    yPositionNext += 8;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.text("City:", leftMargin, yPositionNext);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.text(city, leftMargin + doc.getTextWidth("City:") + 3, yPositionNext);

    yPositionNext += 8;

    autoTable(doc, {
      head: [["Field", "Value"]],
      body: pdfData.slice(1),
      startY: yPositionNext,
      headStyles: {
        fillColor: [128, 0, 32],
        textColor: [255, 255, 255],
        fontStyle: "bold",
      },
    });

    const tableEndY = doc.lastAutoTable.finalY + 5;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.text("Sub Total:", leftMargin, tableEndY);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.text(
      utilService.formatCurrency(calc.subTotal),
      leftMargin + doc.getTextWidth("Sub Total:") + 3,
      tableEndY
    );

    const totalY = tableEndY + 5;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.text("Total:", leftMargin, totalY);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.text(
      utilService.formatCurrency(calc.total),
      leftMargin + doc.getTextWidth("Total:") + 3,
      totalY
    );

    const notesY = totalY + 10;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.text("Notes:", leftMargin, notesY);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.text(note, leftMargin, notesY + 5, { maxWidth: pageWidth - 20 });

    doc.save(`${calc.name}.pdf`);
    fire.sendQuote(doc.output(), `${dealerName}_${customerName}_${city}_${calc.name}`);
    handleModalClose();
  };

  return (
    <>
      <Button variant="outlined" onClick={handleModalOpen}>
        Download PDF
      </Button>

      <Dialog open={isModalOpen} onClose={handleModalClose}>
        <DialogTitle>Enter Your Company Information</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Dealer"
            type="text"
            fullWidth
            id="modal-company-name"
            value={dealerName}
            onChange={handleCompanyNameChange}
          />
          <TextField
            margin="dense"
            label="Customer"
            type="text"
            fullWidth
            value={customerName}
            onChange={handleStateChange}
          />
          <TextField
            margin="dense"
            label="Address"
            type="text"
            fullWidth
            value={address}
            onChange={handleAddressChange}
          />
          <TextField
            margin="dense"
            label="City"
            type="text"
            fullWidth
            value={city}
            onChange={handleCityChange}
          />
          <TextField
            margin="dense"
            label="Notes"
            type="text"
            fullWidth
            value={note}
            onChange={handleNoteChange}
            multiline
            rows={4}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleModalClose}>Cancel</Button>
          <Button onClick={handleDownloadPdf} disabled={!dealerName || !address || !customerName || !city}>
            Submit and Download
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default Excel;
